/**
 * @module components/ui/Checkbox
 * @description [REQ-AUG-30 1.1, ruling Q4] THE one checkbox. Every native `type="checkbox"` in the
 * client renders through here — the probe (`reqaug30-r3-sweep-probe`) counts native checkboxes
 * outside this file and reds on any, so a new screen cannot reintroduce the two-click snap-back
 * class by reaching for `<input type="checkbox">` out of habit.
 *
 * ⚠️ **THIS IS A PRIMITIVE. IT CONTAINS NO MUTATION LOGIC, DELIBERATELY.** Whether a tick saves a
 * draft or fires a server write is the CALLER's decision; the server-bound pattern lives in ONE
 * place, `hooks/useOptimisticToggle`, and a server-bound site is expected to pair the two. Putting
 * optimism in the component would force it on draft-backed forms, which were never broken (Band-0's
 * own finding: the salary and exam panels hold a local draft and never had the bug).
 *
 * ⚠️ **TWO RENDER MODES, BECAUSE HALF THE CENSUS SITES ALREADY SIT INSIDE A `<label>`.**
 *   - **Row mode** (a `label`/`description` prop, or neither prop but no `bare`): renders its OWN
 *     `<label>` with an expanded touch target — padding grown to reach ≥44px and pulled back with an
 *     equal negative margin, so the box model nets to zero and the layout is pixel-identical while
 *     the tappable area is not.
 *   - **Bare mode** (`bare`): renders ONLY the styled input, for sites whose PARENT is already a
 *     `<label>` (the fee-collection charge rows, the elective pills). Nesting a label inside a label
 *     is invalid HTML and double-fires the toggle — the parent label is the ≥44px target there, which
 *     is how those rows meet the hit-target rule today.
 *
 * ⚠️ **VISUALS ARE PASS-THROUGH, NOT HOUSE STYLE.** `size` and `accent` default to the browser's own
 * rendering because the census sites vary (15/16/18px; `var(--p)`, `var(--red)`, unstyled) and the
 * sweep's contract is *pixel-identical to today* — a component that "tidied" every box to one size
 * would have changed twenty screens in the same commit that promised to change none.
 */
import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export interface CheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Renders `aria-checked="mixed"` and the native dash. No census site uses it yet; supported per ruling. */
  indeterminate?: boolean;
  disabled?: boolean;
  /** Row mode: the text beside the box. The whole row toggles. */
  label?: ReactNode;
  /** Row mode: muted second line under the label. */
  description?: ReactNode;
  /** Visual box size in px. Omit for the browser default, which several census sites relied on. */
  size?: number;
  /** `accent-color` for the box. Omit for the browser default. */
  accent?: string;
  /** Parent is already a <label>: render only the input (nested labels are invalid + double-toggle). */
  bare?: boolean;
  /** Extra style on the INPUT itself (marginTop nudges etc. — several sites align the box to text). */
  inputStyle?: CSSProperties;
  /** Style on the wrapping label in row mode (gap/margin tweaks at call sites). */
  style?: CSSProperties;
  id?: string;
  'aria-label'?: string;
  /** data-* markers pass straight through to the INPUT so existing probes keep their anchors. */
  [dataAttr: `data-${string}`]: string | undefined;
}

/** 44px target from a ~16px box: 14px padding each side. Pulled back by the same margin → zero layout shift. */
const TOUCH_PAD = 14;

export function Checkbox(props: CheckboxProps): JSX.Element {
  const {
    checked, onChange, indeterminate, disabled, label, description,
    size, accent, bare, inputStyle, style, id, ...rest
  } = props;
  const ref = useRef<HTMLInputElement>(null);

  // `indeterminate` is a DOM property, not an attribute — it can only be set imperatively.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  const input = (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-checked={indeterminate ? 'mixed' : checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{
        ...(size !== undefined ? { width: size, height: size } : {}),
        ...(accent !== undefined ? { accentColor: accent } : {}),
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        ...inputStyle,
      }}
      {...rest}
    />
  );

  if (bare) return input;

  return (
    <label
      htmlFor={id}
      data-checkbox-row=""
      style={{
        display: 'inline-flex',
        alignItems: description ? 'flex-start' : 'center',
        gap: label || description ? 8 : 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // The expanded touch target: grow the tappable box, net the layout to zero.
        padding: TOUCH_PAD,
        margin: -TOUCH_PAD,
        userSelect: 'none',
        ...style,
      }}
    >
      {input}
      {(label || description) && (
        <span style={{ minWidth: 0 }}>
          {label}
          {description && (
            <>
              <br />
              <span style={{ color: 'var(--t3)' }}>{description}</span>
            </>
          )}
        </span>
      )}
    </label>
  );
}

export default Checkbox;
